import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { formatEUR } from "../../lib/format";
import {
  Radar, Loader2, X, Save, Settings2, Zap, TrendingUp, TrendingDown,
  Minus, Check, Plus, Trash2, ChevronRight, PowerOff, Power, Sparkles, ShoppingBag,
} from "lucide-react";

/**
 * Price Radar — Dynamic Pricing Intelligence Engine (v3.7).
 *
 * Adds a master ON/OFF toggle, an AI-credit ledger with lazy daily grants,
 * per-scan credit deduction and an insufficient-credits top-up modal.
 */

// User-facing date formatter — strict DD/MM/YYYY.
const formatEUDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

const TOPUP_TIERS = [
  { tier: "100", credits: 100, eur: 29 },
  { tier: "250", credits: 250, eur: 59 },
  { tier: "600", credits: 600, eur: 99 },
];

const WINDOW_PRESETS = [
  { key: "next-3d",   label: "Next 3 days",     days: 3,  fromToday: true  },
  { key: "next-14d",  label: "+14 days",        days: 14, fromToday: true  },
  { key: "high-jul",  label: "High Season · July",     start: () => `${new Date().getFullYear()}-07-01`, end: () => `${new Date().getFullYear()}-07-31` },
  { key: "high-aug",  label: "High Season · August",   start: () => `${new Date().getFullYear()}-08-01`, end: () => `${new Date().getFullYear()}-08-31` },
];

const isoToday = (offset = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
};

function resolvePreset(preset) {
  if (preset.fromToday) return { start: isoToday(0), end: isoToday(preset.days - 1) };
  return { start: preset.start(), end: preset.end() };
}

export default function PriceRadar() {
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [windowKey, setWindowKey] = useState("next-14d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const [applyModal, setApplyModal] = useState(null);   // { row } when open
  const [topupOpen, setTopupOpen] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);

  const activePreset = WINDOW_PRESETS.find((p) => p.key === windowKey);
  const range = useMemo(() => {
    if (windowKey === "custom") {
      return { start: customStart, end: customEnd };
    }
    return activePreset ? resolvePreset(activePreset) : { start: isoToday(0), end: isoToday(13) };
  }, [windowKey, customStart, customEnd, activePreset]);

  // Bootstrap locations + matrix.
  useEffect(() => {
    api.get("/settings/locations")
      .then((r) => setLocations(Array.isArray(r.data) ? r.data : []))
      .catch(() => setLocations([]));
  }, []);

  const loadMatrix = async () => {
    if (!range.start || !range.end) return;
    setLoading(true);
    try {
      const { data } = await api.get("/admin/price-radar/matrix", {
        params: {
          start_date:  range.start,
          end_date:    range.end,
          location_id: locationId || undefined,
        },
      });
      setMatrix(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
      setMatrix(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadMatrix(); /* eslint-disable-next-line */ }, [range.start, range.end, locationId]);

  const scan = async () => {
    if (!range.start || !range.end) {
      toast.warning("Pick a date window first.");
      return;
    }
    // Guard: prevent scan when master switch is off (backend also enforces).
    if (matrix?.settings && matrix.settings.is_active === false) {
      toast.warning("Radar is disabled. Enable it before scanning.");
      return;
    }
    // Guard: open the top-up modal locally when balance is under the floor.
    if ((matrix?.settings?.credit_balance ?? 0) < 5) {
      setTopupOpen(true);
      return;
    }
    setScanning(true);
    try {
      const { data } = await api.post("/admin/price-radar/scan", {
        start_date:  range.start,
        end_date:    range.end,
        location_id: locationId || null,
        window_key:  windowKey,
      });
      toast.success(`Scanned market rates`, {
        description: `${data.scanned} snapshots captured · −${data.tokens_spent} AI credits · balance ${data.credit_balance}`,
      });
      await loadMatrix();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.detail : null;
      if (code === "INSUFFICIENT_CREDITS") {
        setTopupOpen(true);
      } else if (code === "RADAR_DISABLED") {
        toast.error(detail?.message || "Radar disabled");
      } else {
        toast.error(formatApiError(detail) || err.message);
      }
      await loadMatrix();
    } finally {
      setScanning(false);
    }
  };

  // Master ON/OFF toggle — persists instantly.
  const toggleActive = async () => {
    if (!matrix?.settings) return;
    const next = !matrix.settings.is_active;
    setTogglingActive(true);
    try {
      const payload = {
        monitored_categories: matrix.settings.monitored_categories,
        competitors:          matrix.settings.competitors,
        rules:                matrix.settings.rules,
        operating_region:     matrix.settings.operating_region,
        is_active:            next,
      };
      await api.put("/admin/price-radar/settings", payload);
      toast.success(next ? "Radar ON · Monitoring active" : "Radar OFF · Inactive");
      await loadMatrix();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setTogglingActive(false);
    }
  };

  const purchaseTopup = async (tier) => {
    try {
      const { data } = await api.post("/admin/price-radar/topup", { tier });
      toast.success(`+${data.granted} AI credits`, { description: `New balance: ${data.credit_balance}` });
      setTopupOpen(false);
      await loadMatrix();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    }
  };

  const applyRow = (row) => {
    setApplyModal({ row });
  };

  const submitApply = async ({ row, editedRate }) => {
    const target = Number(editedRate);
    if (!(target > 0)) {
      toast.error("Enter a valid rate greater than zero.");
      return;
    }
    try {
      const { data } = await api.post("/admin/price-radar/apply", {
        start_date:  range.start,
        end_date:    range.end,
        location_id: locationId || null,
        rows: [{
          category:  row.category,
          new_rate:  target,
          season_id: row.season_id || null,
        }],
      });
      toast.success(`Updated ${data.written} vehicle${data.written === 1 ? "" : "s"}`, {
        description: `${row.category} → €${target.toFixed(2)}/day`,
      });
      setApplyModal(null);
      await loadMatrix();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    }
  };

  const applyAll = async () => {
    const actionable = (matrix?.rows || []).filter(
      (r) => r.recommendation.action !== "keep",
    );
    if (actionable.length === 0) {
      toast.info("Every category is already optimal.");
      return;
    }
    setConfirmAll(true);
  };

  const runApplyAll = async () => {
    const actionable = (matrix?.rows || []).filter(
      (r) => r.recommendation.action !== "keep",
    );
    setConfirmAll(false);
    try {
      const { data } = await api.post("/admin/price-radar/apply", {
        start_date:  range.start,
        end_date:    range.end,
        location_id: locationId || null,
        rows: actionable.map((r) => ({
          category:  r.category,
          new_rate:  r.recommendation.target_rate,
          season_id: r.season_id || null,
        })),
      });
      toast.success(`Bulk apply complete`, { description: `${data.written} vehicles repriced` });
      await loadMatrix();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    }
  };

  const rows = matrix?.rows || [];
  const settings = matrix?.settings;
  const nothingConfigured = settings && settings.monitored_categories.length === 0;

  return (
    <div data-testid="price-radar-page" className="max-w-[1920px] mx-auto">
      {/* ── Header ────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Intelligence</p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2 flex items-center gap-3">
            <Radar size={28} /> Price radar.
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 max-w-2xl">
            Live utilization × competitor rates × recommended repricing (all values are strict <strong>€/day</strong>).
            Approve individual moves or apply everything in one confident click.
          </p>
          {settings?.operating_region && (
            <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border border-black/10 dark:border-white/10 text-neutral-600 dark:text-neutral-300" data-testid="pr-region-badge">
              Operating region · <strong className="font-medium">{settings.operating_region}</strong>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {/* Credit pill + top-up trigger */}
          {settings && (
            <div
              className="inline-flex items-center gap-2 rounded-full border border-amber-300/60 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200 pl-3 pr-1 py-1"
              data-testid="pr-credit-pill"
            >
              <Sparkles size={13} />
              <span className="text-xs font-semibold tabular-nums" data-testid="pr-credit-balance">
                {settings.credit_balance} AI Credits
              </span>
              <button
                type="button"
                onClick={() => setTopupOpen(true)}
                className="ml-1 text-[11px] px-2.5 py-1 rounded-full bg-amber-600 hover:bg-amber-700 text-white inline-flex items-center gap-1"
                data-testid="pr-topup-btn"
              >
                <Plus size={11} /> Buy credits
              </button>
            </div>
          )}
          {/* Master ON/OFF toggle */}
          {settings && (
            <button
              type="button"
              onClick={toggleActive}
              disabled={togglingActive}
              className={`text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-2 border transition-colors ${
                settings.is_active
                  ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700"
                  : "bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 border-neutral-300 dark:border-neutral-700"
              }`}
              data-testid="pr-master-toggle"
              aria-pressed={settings.is_active}
              title={settings.is_active ? "Click to pause radar" : "Click to resume radar"}
            >
              {settings.is_active ? <Power size={12} /> : <PowerOff size={12} />}
              {settings.is_active ? "Radar ON · Monitoring active" : "Radar OFF · Inactive"}
            </button>
          )}
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="text-sm px-4 py-2.5 rounded border border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white inline-flex items-center gap-2"
            data-testid="pr-manage-btn"
          >
            <Settings2 size={14} /> Manage competitors &amp; categories
          </button>
          <button
            type="button"
            onClick={scan}
            disabled={scanning || nothingConfigured || (settings && !settings.is_active)}
            className="bg-black text-white dark:bg-white dark:text-black text-sm px-5 py-2.5 rounded inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="pr-scan-btn"
          >
            {scanning ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
            {scanning ? "Scanning…" : "Fetch / Scan market rates"}
          </button>
        </div>
      </div>

      {/* ── Disabled banner ───────────────────────────────────────── */}
      {settings && !settings.is_active && (
        <div
          className="mb-6 border border-neutral-300 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 rounded-lg px-5 py-4 flex items-center gap-3 text-sm text-neutral-700 dark:text-neutral-300"
          data-testid="pr-disabled-banner"
        >
          <PowerOff size={16} className="text-neutral-500 shrink-0" />
          <p>Price Radar is temporarily disabled. No AI credits are being consumed.</p>
        </div>
      )}

      {/* ── Filter bar ────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded-lg p-4 mb-6 flex flex-wrap items-end gap-4" data-testid="pr-controls">
        <ControlBlock label="Pickup location">
          <select
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            className="editorial-input min-w-[220px]"
            data-testid="pr-location-select"
          >
            <option value="">All locations</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        </ControlBlock>
        <ControlBlock label="Date window">
          <div className="flex flex-wrap gap-1.5" data-testid="pr-window-chips">
            {WINDOW_PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setWindowKey(p.key)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  windowKey === p.key
                    ? "bg-black text-white dark:bg-white dark:text-black border-black dark:border-white"
                    : "border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white"
                }`}
                data-testid={`pr-window-${p.key}`}
              >
                {p.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setWindowKey("custom")}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                windowKey === "custom"
                  ? "bg-black text-white dark:bg-white dark:text-black border-black dark:border-white"
                  : "border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white"
              }`}
              data-testid="pr-window-custom"
            >
              Custom
            </button>
          </div>
        </ControlBlock>
        {windowKey === "custom" && (
          <>
            <ControlBlock label="Start">
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="editorial-input"
                data-testid="pr-custom-start"
              />
            </ControlBlock>
            <ControlBlock label="End">
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="editorial-input"
                data-testid="pr-custom-end"
              />
            </ControlBlock>
          </>
        )}
        <div className="ml-auto text-xs text-neutral-500 dark:text-neutral-400" data-testid="pr-range-summary">
          {range.start && range.end ? (
            <>Window: <span className="font-medium text-neutral-700 dark:text-neutral-200">{formatEUDate(range.start)} → {formatEUDate(range.end)}</span></>
          ) : "Select a window"}
        </div>
      </div>

      {/* ── Matrix ───────────────────────────────────────────────── */}
      <div
        className={`bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded-lg overflow-hidden transition-opacity ${
          settings && !settings.is_active ? "opacity-50 pointer-events-none" : ""
        }`}
        data-testid="pr-matrix"
        aria-disabled={settings && !settings.is_active ? "true" : "false"}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-black/8 dark:border-white/10 bg-[#F3F4F6] dark:bg-[#111827]">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Radar intelligence matrix</p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
              {rows.length} monitored categor{rows.length === 1 ? "y" : "ies"} · last scan {matrix?.generated_at ? new Date(matrix.generated_at).toLocaleTimeString() : "—"}
            </p>
          </div>
          <button
            type="button"
            onClick={applyAll}
            disabled={loading || rows.length === 0 || (settings && !settings.is_active)}
            className="text-sm px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="pr-apply-all-btn"
          >
            <Check size={14} /> Approve &amp; apply all
          </button>
        </div>
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400 bg-white dark:bg-[#1F2937]">
            <tr className="border-b border-black/8 dark:border-white/10">
              <th className="py-3 px-5 font-medium">Fleet category</th>
              <th className="py-3 px-4 font-medium">Utilization</th>
              <th className="py-3 px-4 font-medium text-right">Market min €/day</th>
              <th className="py-3 px-4 font-medium text-right">Market median €/day</th>
              <th className="py-3 px-4 font-medium text-right">Market max €/day</th>
              <th className="py-3 px-4 font-medium text-right">Our rate €/day</th>
              <th className="py-3 px-4 font-medium">Recommendation</th>
              <th className="py-3 px-4 font-medium text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="py-10 text-center text-neutral-500"><Loader2 size={16} className="inline animate-spin" /> Loading matrix…</td></tr>
            )}
            {!loading && nothingConfigured && (
              <tr><td colSpan={8} className="py-16 text-center text-neutral-500 dark:text-neutral-400">
                No monitored categories yet. Open <span className="underline">Manage competitors &amp; categories</span> to pick up to five categories to track.
              </td></tr>
            )}
            {!loading && !nothingConfigured && rows.length === 0 && (
              <tr><td colSpan={8} className="py-16 text-center text-neutral-500 dark:text-neutral-400">No data for this window yet.</td></tr>
            )}
            {!loading && rows.map((r) => (
              <MatrixRow key={r.category} row={r} onApply={() => applyRow(r)} />
            ))}
          </tbody>
        </table>
      </div>

      {drawerOpen && (
        <ManageDrawer
          settings={settings}
          onClose={() => setDrawerOpen(false)}
          onSaved={async () => {
            setDrawerOpen(false);
            await loadMatrix();
          }}
        />
      )}

      {confirmAll && (
        <ApplyAllModal
          rows={rows.filter((r) => r.recommendation.action !== "keep")}
          onCancel={() => setConfirmAll(false)}
          onConfirm={runApplyAll}
        />
      )}

      {applyModal && (
        <ApplyRateModal
          row={applyModal.row}
          onCancel={() => setApplyModal(null)}
          onSubmit={submitApply}
        />
      )}

      {topupOpen && (
        <TopUpModal
          balance={settings?.credit_balance ?? 0}
          onClose={() => setTopupOpen(false)}
          onBuy={purchaseTopup}
        />
      )}
    </div>
  );
}

function ControlBlock({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.25em] text-neutral-500 dark:text-neutral-400 mb-2">{label}</span>
      {children}
    </label>
  );
}

function MatrixRow({ row, onApply }) {
  const util = row.utilization;
  const utilTone =
    util > 75 ? "text-rose-600 dark:text-rose-400" :
    util < 40 ? "text-amber-600 dark:text-amber-400" :
    "text-emerald-600 dark:text-emerald-400";
  return (
    <tr className="border-t border-black/5 dark:border-white/5" data-testid={`pr-matrix-row-${row.category}`}>
      <td className="py-3 px-5 font-medium capitalize" data-testid={`pr-row-cat-${row.category}`}>
        {row.category}
        <div className="text-[10px] text-neutral-500 dark:text-neutral-400 mt-0.5">
          {row.vehicle_count} group{row.vehicle_count === 1 ? "" : "s"} · {row.fleet_units} unit{row.fleet_units === 1 ? "" : "s"}
        </div>
      </td>
      <td className="py-3 px-4">
        <span className={`text-sm tabular-nums font-semibold ${utilTone}`} data-testid={`pr-row-util-${row.category}`}>
          {util.toFixed(1)}%
        </span>
        <div className="text-[10px] text-neutral-500 dark:text-neutral-400 mt-0.5">
          {row.booked_units}/{row.fleet_units} booked
        </div>
      </td>
      <td className="py-3 px-4 text-right tabular-nums text-neutral-600 dark:text-neutral-300">
        {row.market_samples > 0 ? <>{formatEUR(row.market_min)}<span className="text-[10px] text-neutral-400 ml-0.5">/day</span></> : "—"}
      </td>
      <td className="py-3 px-4 text-right tabular-nums font-medium">
        {row.market_samples > 0 ? <>{formatEUR(row.market_median)}<span className="text-[10px] text-neutral-400 ml-0.5">/day</span></> : "—"}
      </td>
      <td className="py-3 px-4 text-right tabular-nums text-neutral-600 dark:text-neutral-300">
        {row.market_samples > 0 ? <>{formatEUR(row.market_max)}<span className="text-[10px] text-neutral-400 ml-0.5">/day</span></> : "—"}
      </td>
      <td className="py-3 px-4 text-right tabular-nums font-semibold" data-testid={`pr-row-ourrate-${row.category}`}>
        {formatEUR(row.our_rate)}<span className="text-[10px] text-neutral-400 ml-0.5">/day</span>
      </td>
      <td className="py-3 px-4">
        <RecommendationBadge rec={row.recommendation} />
      </td>
      <td className="py-3 px-4 text-right">
        <button
          type="button"
          onClick={onApply}
          className="text-xs px-3 py-1.5 rounded border border-black dark:border-white bg-black text-white dark:bg-white dark:text-black hover:opacity-90 inline-flex items-center gap-1"
          data-testid={`pr-apply-${row.category}`}
        >
          Apply rate <ChevronRight size={12} />
        </button>
      </td>
    </tr>
  );
}

function RecommendationBadge({ rec }) {
  const map = {
    raise: { icon: <TrendingUp size={12} />, cls: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800" },
    drop:  { icon: <TrendingDown size={12} />, cls: "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800" },
    keep:  { icon: <Minus size={12} />, cls: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800" },
  };
  const style = map[rec.action] || map.keep;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border ${style.cls}`}
      data-testid={`pr-rec-badge-${rec.action}`}
    >
      {style.icon} {rec.label}
      <span className="opacity-70">· {rec.reason}</span>
    </span>
  );
}

// ── Manage competitors & categories drawer ────────────────────────────

function ManageDrawer({ settings, onClose, onSaved }) {
  const [categories, setCategories] = useState(settings?.monitored_categories || []);
  const [competitors, setCompetitors] = useState(settings?.competitors || []);
  const [rules, setRules] = useState(settings?.rules || {
    high_utilization_threshold: 75,
    low_utilization_threshold:  40,
    markup_step: 10,
    markdown_step: 10,
  });
  const [operatingRegion, setOperatingRegion] = useState(settings?.operating_region || "Brainart Master Demo");
  const [saving, setSaving] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);

  useEffect(() => {
    api.get("/vehicles")
      .then((r) => {
        const cats = Array.from(new Set((r.data || []).map((v) => (v.category || "").toLowerCase()).filter(Boolean))).sort();
        setAvailableCategories(cats);
      })
      .catch(() => setAvailableCategories([]));
  }, []);

  const toggleCategory = (cat) => {
    setCategories((prev) => {
      if (prev.includes(cat)) return prev.filter((c) => c !== cat);
      if (prev.length >= 5) {
        toast.warning("Max 5 categories monitored.");
        return prev;
      }
      return [...prev, cat];
    });
  };

  const addCompetitor = () => {
    if (competitors.length >= 10) {
      toast.warning("Max 10 competitors.");
      return;
    }
    setCompetitors((prev) => [...prev, { id: crypto.randomUUID?.() || String(Date.now()), name: "", domain: "", is_active: true }]);
  };
  const updateCompetitor = (idx, patch) => {
    setCompetitors((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  };
  const removeCompetitor = (idx) => {
    setCompetitors((prev) => prev.filter((_, i) => i !== idx));
  };
  const updateRule = (k, v) => {
    setRules((prev) => ({ ...prev, [k]: v }));
  };

  const save = async () => {
    // Basic validation
    for (const c of competitors) {
      if (!c.name.trim() || !c.domain.trim()) {
        toast.error("Every competitor needs a name and a domain.");
        return;
      }
    }
    const nums = ["high_utilization_threshold", "low_utilization_threshold", "markup_step", "markdown_step"];
    for (const k of nums) {
      if (!(Number(rules[k]) > 0)) {
        toast.error(`Rule "${k}" must be > 0`);
        return;
      }
    }
    if (Number(rules.low_utilization_threshold) >= Number(rules.high_utilization_threshold)) {
      toast.error("Low utilization threshold must be less than high threshold.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/price-radar/settings", {
        monitored_categories: categories,
        competitors: competitors.map((c) => ({
          id:        c.id,
          name:      c.name.trim(),
          domain:    c.domain.trim(),
          is_active: !!c.is_active,
        })),
        rules: {
          high_utilization_threshold: Number(rules.high_utilization_threshold),
          low_utilization_threshold:  Number(rules.low_utilization_threshold),
          markup_step:                Number(rules.markup_step),
          markdown_step:              Number(rules.markdown_step),
        },
        operating_region: (operatingRegion || "Brainart Master Demo").trim() || "Brainart Master Demo",
      });
      toast.success("Radar settings saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex justify-end" data-testid="pr-drawer" onClick={onClose}>
      <div
        className="bg-white dark:bg-[#1F2937] w-full max-w-2xl h-full overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-black/8 dark:border-white/10 bg-[#F3F4F6] dark:bg-[#111827] flex items-center justify-between sticky top-0 z-10">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Radar configuration</p>
            <h2 className="font-display text-2xl mt-1">Competitors &amp; categories</h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-black/5 dark:hover:bg-white/10 rounded" data-testid="pr-drawer-close">
            <X size={18} />
          </button>
        </header>

        <div className="p-6 space-y-8">
          {/* Operating Region */}
          <section>
            <h3 className="text-sm font-medium mb-1">Operating region / island</h3>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
              Bounds market scans to your operational territory. Local pickup points (Airport, Port, Main Office) still apply on top.
            </p>
            <input
              type="text"
              value={operatingRegion}
              onChange={(e) => setOperatingRegion(e.target.value)}
              className="editorial-input w-full max-w-md"
              placeholder="e.g. Brainart Master Demo"
              data-testid="pr-drawer-region"
            />
          </section>

          {/* Categories */}
          <section>
            <h3 className="text-sm font-medium mb-1">Monitored fleet categories</h3>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
              Pick up to 5 categories. Selection: <strong>{categories.length}/5</strong>.
            </p>
            <div className="flex flex-wrap gap-2" data-testid="pr-drawer-cats">
              {availableCategories.length === 0 && (
                <p className="text-xs text-neutral-500 italic">No categories exist yet. Add vehicles to your fleet first.</p>
              )}
              {availableCategories.map((c) => {
                const on = categories.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleCategory(c)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors capitalize ${
                      on
                        ? "bg-black text-white dark:bg-white dark:text-black border-black dark:border-white"
                        : "border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white"
                    }`}
                    data-testid={`pr-drawer-cat-${c}`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Competitors */}
          <section>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-medium">Competitors ({competitors.length}/10)</h3>
              <button
                type="button"
                onClick={addCompetitor}
                className="text-xs px-3 py-1.5 rounded border border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white inline-flex items-center gap-1"
                data-testid="pr-drawer-add-competitor"
              >
                <Plus size={12} /> Add competitor
              </button>
            </div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
              Active competitors are polled on each market scan.
            </p>
            <div className="space-y-2">
              {competitors.length === 0 && (
                <p className="text-xs text-neutral-500 italic">No competitors yet.</p>
              )}
              {competitors.map((c, i) => (
                <div key={c.id || i} className="flex items-center gap-2" data-testid={`pr-drawer-competitor-${i}`}>
                  <input
                    value={c.name}
                    onChange={(e) => updateCompetitor(i, { name: e.target.value })}
                    placeholder="Name (e.g. Hertz)"
                    className="editorial-input flex-1"
                    data-testid={`pr-comp-name-${i}`}
                  />
                  <input
                    value={c.domain}
                    onChange={(e) => updateCompetitor(i, { domain: e.target.value })}
                    placeholder="Domain (e.g. hertz.com)"
                    className="editorial-input flex-1"
                    data-testid={`pr-comp-domain-${i}`}
                  />
                  <label className="flex items-center gap-1.5 text-xs text-neutral-600 dark:text-neutral-300 shrink-0">
                    <input
                      type="checkbox"
                      checked={!!c.is_active}
                      onChange={(e) => updateCompetitor(i, { is_active: e.target.checked })}
                      data-testid={`pr-comp-active-${i}`}
                    />
                    Active
                  </label>
                  <button
                    type="button"
                    onClick={() => removeCompetitor(i)}
                    className="p-1.5 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded shrink-0"
                    data-testid={`pr-comp-remove-${i}`}
                    aria-label="Remove"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          </section>

          {/* Rules */}
          <section>
            <h3 className="text-sm font-medium mb-1">Repricing rules</h3>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
              Thresholds and step sizes that drive the auto-recommendation badges.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <RuleField label="High utilization threshold (%)" value={rules.high_utilization_threshold} onChange={(v) => updateRule("high_utilization_threshold", v)} testId="pr-rule-hi" />
              <RuleField label="Low utilization threshold (%)"  value={rules.low_utilization_threshold}  onChange={(v) => updateRule("low_utilization_threshold", v)}  testId="pr-rule-lo" />
              <RuleField label="Markup step (€)"    value={rules.markup_step}   onChange={(v) => updateRule("markup_step", v)}   testId="pr-rule-up" />
              <RuleField label="Markdown step (€)"  value={rules.markdown_step} onChange={(v) => updateRule("markdown_step", v)} testId="pr-rule-down" />
            </div>
          </section>
        </div>

        <footer className="px-6 py-4 border-t border-black/8 dark:border-white/10 bg-[#F3F4F6] dark:bg-[#111827] flex justify-end gap-3 sticky bottom-0">
          <button type="button" onClick={onClose} className="text-sm px-4 py-2" data-testid="pr-drawer-cancel">
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="bg-black text-white dark:bg-white dark:text-black text-sm px-5 py-2 rounded inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="pr-drawer-save"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save configuration
          </button>
        </footer>
      </div>
    </div>
  );
}

function RuleField({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.25em] text-neutral-500 dark:text-neutral-400 mb-2">{label}</span>
      <input
        type="number"
        step="0.5"
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="editorial-input w-full"
        data-testid={testId}
      />
    </label>
  );
}

// ── Apply-all confirmation modal (shows diffs) ────────────────────────

function ApplyAllModal({ rows, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onCancel} data-testid="pr-apply-all-modal">
      <div
        className="bg-white dark:bg-[#1F2937] w-full max-w-2xl rounded-lg shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-black/8 dark:border-white/10 bg-[#F3F4F6] dark:bg-[#111827]">
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Confirm bulk apply</p>
          <h2 className="font-display text-2xl mt-1">Review {rows.length} price change{rows.length === 1 ? "" : "s"}</h2>
        </header>
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400">
              <tr>
                <th className="py-2">Category</th>
                <th className="py-2">Action</th>
                <th className="py-2 text-right">Current €/day</th>
                <th className="py-2 text-right">Target €/day</th>
                <th className="py-2 text-right">Δ €/day</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const delta = r.recommendation.target_rate - r.our_rate;
                const deltaSign = delta > 0 ? "+" : "";
                const cls = delta > 0
                  ? "text-rose-600 dark:text-rose-400"
                  : delta < 0
                    ? "text-amber-600 dark:text-amber-400"
                    : "";
                return (
                  <tr key={r.category} className="border-t border-black/5 dark:border-white/5" data-testid={`pr-apply-all-row-${r.category}`}>
                    <td className="py-2 capitalize font-medium">{r.category}</td>
                    <td className="py-2"><RecommendationBadge rec={r.recommendation} /></td>
                    <td className="py-2 text-right tabular-nums">{formatEUR(r.our_rate)}</td>
                    <td className="py-2 text-right tabular-nums font-semibold">{formatEUR(r.recommendation.target_rate)}</td>
                    <td className={`py-2 text-right tabular-nums ${cls}`}>{deltaSign}{formatEUR(delta)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <footer className="px-6 py-4 border-t border-black/8 dark:border-white/10 bg-[#F3F4F6] dark:bg-[#111827] flex justify-end gap-3">
          <button type="button" onClick={onCancel} className="text-sm px-4 py-2" data-testid="pr-apply-all-cancel">Cancel</button>
          <button
            type="button"
            onClick={onConfirm}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-5 py-2 rounded inline-flex items-center gap-2"
            data-testid="pr-apply-all-confirm"
          >
            <Check size={13} /> Apply all
          </button>
        </footer>
      </div>
    </div>
  );
}


// ── Editable per-row Apply Rate modal ─────────────────────────────────

function ApplyRateModal({ row, onCancel, onSubmit }) {
  const suggested = row.recommendation.target_rate;
  const [editedRate, setEditedRate] = useState(String(suggested ?? row.our_rate ?? ""));
  const [saving, setSaving] = useState(false);

  const parsed = Number(editedRate);
  const invalid = !(parsed > 0);
  const delta = invalid ? 0 : parsed - row.our_rate;
  const deltaSign = delta > 0 ? "+" : "";
  const deltaCls = delta > 0 ? "text-rose-600 dark:text-rose-400"
                : delta < 0 ? "text-amber-600 dark:text-amber-400"
                : "text-neutral-500";

  const handleSubmit = async () => {
    if (invalid) return;
    setSaving(true);
    try {
      await onSubmit({ row, editedRate: parsed });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="pr-apply-modal"
    >
      <div
        className="bg-white dark:bg-[#1F2937] w-full max-w-md rounded-lg shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-black/8 dark:border-white/10 bg-[#F3F4F6] dark:bg-[#111827]">
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Apply new rate</p>
          <h2 className="font-display text-2xl mt-1 capitalize">{row.category}</h2>
        </header>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-3 gap-3 text-xs">
            <Stat label="Current" value={<>{formatEUR(row.our_rate)}<span className="text-[10px] text-neutral-400"> /day</span></>} />
            <Stat label="Recommended" value={<>{formatEUR(suggested)}<span className="text-[10px] text-neutral-400"> /day</span></>} />
            <Stat label="Δ vs current" value={<span className={deltaCls}>{invalid ? "—" : `${deltaSign}${formatEUR(delta)}`}</span>} />
          </div>

          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-neutral-500 dark:text-neutral-400 mb-2">
              New daily rate (€/day)
            </span>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 text-sm pointer-events-none">€</span>
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0.01"
                autoFocus
                value={editedRate}
                onChange={(e) => setEditedRate(e.target.value)}
                className="editorial-input w-full pl-7 pr-14 text-lg tabular-nums"
                data-testid="pr-apply-rate-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-neutral-400 pointer-events-none">/day</span>
            </div>
            {invalid && (
              <p className="text-xs text-rose-600 mt-1.5" data-testid="pr-apply-rate-error">
                Enter a rate greater than zero.
              </p>
            )}
          </label>

          <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed">
            {row.season_id
              ? "Writes to the seasonal-rate row that overlaps your date window. Existing duration tiers are preserved."
              : "Writes to the base daily rate of every vehicle in this category."}
          </p>
        </div>

        <footer className="px-6 py-4 border-t border-black/8 dark:border-white/10 bg-[#F3F4F6] dark:bg-[#111827] flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm px-4 py-2"
            data-testid="pr-apply-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={invalid || saving}
            className="bg-black text-white dark:bg-white dark:text-black text-sm px-5 py-2 rounded inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="pr-apply-submit"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
            Apply €{parsed > 0 ? parsed.toFixed(2) : "—"}/day
          </button>
        </footer>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400">{label}</p>
      <p className="mt-1 text-sm font-semibold tabular-nums">{value}</p>
    </div>
  );
}

// ── Insufficient-credits / top-up modal ───────────────────────────────

function TopUpModal({ balance, onClose, onBuy }) {
  const [busy, setBusy] = useState(null);   // tier being purchased
  const empty = balance < 5;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="pr-topup-modal"
    >
      <div
        className="bg-white dark:bg-[#1F2937] w-full max-w-2xl rounded-lg shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-black/8 dark:border-white/10 bg-[#F3F4F6] dark:bg-[#111827] flex items-start justify-between">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">
              {empty ? "No AI Radar credits left" : "AI Radar Credits"}
            </p>
            <h2 className="font-display text-2xl mt-1">
              {empty ? "You have run out of credits." : `You have ${balance} credits.`}
            </h2>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 max-w-md">
              {empty
                ? "You have run out of market-analysis credits. A free top-up (5 credits) lands every morning, or pick a package for unlimited scans."
                : "Boost the radar for unlimited market scans."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-black/5 dark:hover:bg-white/10 rounded"
            data-testid="pr-topup-close"
          >
            <X size={18} />
          </button>
        </header>

        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pr-topup-tiers">
          {TOPUP_TIERS.map((t) => (
            <button
              key={t.tier}
              type="button"
              onClick={async () => {
                setBusy(t.tier);
                try { await onBuy(t.tier); } finally { setBusy(null); }
              }}
              disabled={busy !== null}
              className="text-left border border-black/10 dark:border-white/10 rounded-lg p-5 hover:border-black dark:hover:border-white transition-colors disabled:opacity-50 group"
              data-testid={`pr-topup-tier-${t.tier}`}
            >
              <div className="flex items-center justify-between">
                <ShoppingBag size={16} className="text-neutral-400 group-hover:text-black dark:group-hover:text-white transition-colors" />
                {busy === t.tier && <Loader2 size={14} className="animate-spin" />}
              </div>
              <p className="mt-4 font-display text-3xl tabular-nums">{t.credits}</p>
              <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400 mt-1">AI Credits</p>
              <p className="mt-4 text-sm">
                <span className="font-semibold tabular-nums">€{t.eur}</span>
                <span className="text-neutral-500"> · one-time</span>
              </p>
              <p className="mt-4 text-[11px] text-neutral-500 dark:text-neutral-400">
                ≈ {Math.floor(t.credits / 10)} full scans
              </p>
            </button>
          ))}
        </div>

        <footer className="px-6 py-4 border-t border-black/8 dark:border-white/10 bg-[#F3F4F6] dark:bg-[#111827] flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-4 py-2"
            data-testid="pr-topup-later"
          >
            Later
          </button>
        </footer>
      </div>
    </div>
  );
}

